import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { defaultFormConfig as defaultEventFormConfig, eventFormConfig } from '../config/eventFormConfig';
// import { commonFormFields } from '../types/formFields';

interface UserInfoStepProps {
  onNext: (data: Record<string, any>) => void;
  initialData?: Record<string, any>;
  formType?: string;
  setStepValidity?: (isValid: boolean) => void;
  namespace?: string;
}

export const UserInfoStep: React.FC<UserInfoStepProps> = ({ 
  onNext,
  initialData = {},
  formType = 'special-event',
  setStepValidity,
  namespace = 'events'
}) => {
  // Use special-event as fallback for unknown form types
  let formConfig = eventFormConfig['special-event'] || defaultEventFormConfig;
  if (formType && eventFormConfig[formType]) {
    formConfig = eventFormConfig[formType];
  }

  // Load both the specified namespace and appointments namespace
  const { t } = useTranslation([namespace, 'appointments', 'home']);
  const [phone, setPhone] = React.useState(initialData.phone || '');
  
  // Form validation state
  const [formValues, setFormValues] = useState<Record<string, any>>({...initialData, phone});
  const [formValid, setFormValid] = useState(false);

  // Validate the form whenever values change
  useEffect(() => {
    validateForm();
  }, [formValues, phone]);

  // Update step validity whenever formValid changes
  useEffect(() => {
    if (setStepValidity) {
      setStepValidity(formValid);
    }
  }, [formValid, setStepValidity]);

  // Validate all required fields
  const validateForm = () => {
    // Get all required field IDs
    const requiredFields = formConfig.fields
      .filter(field => field.required)
      .map(field => field.id);

    // Check if all required fields have values
    const allRequiredFieldsHaveValues = requiredFields.every(fieldId => {
      // Special handling for phone field which is stored in state
      if (fieldId === 'phone') {
        return phone && phone.length >= 10; // Basic validation for phone numbers
      }
      return formValues[fieldId] && formValues[fieldId].trim() !== '';
    });

    setFormValid(allRequiredFieldsHaveValues);
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());
    console.log('UserInfoStep form data:', { ...data, phone });
    onNext({ ...data, phone }); // Include phone from state
  };

  // Handle input changes to update validation state
  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };


  return (
    <form id="current-step-form" onSubmit={handleSubmit} className="user-info-form">
      {formConfig.layout.rows.map((row: string[], rowIndex: number) => (
        <div key={rowIndex} className={`form-row ${row.length === 1 || row.some(id => {
          const field = formConfig.fields.find(f => f.id === id);
          return field?.type === 'textarea';
        }) ? 'full-width' : ''}`}>
          {row.map((fieldId: string) => {
            const field = formConfig.fields.find((f: { id: string }) => f.id === fieldId);
            if (!field) return null;

            return (
              <div key={field.id} className={`form-field ${field.type === 'radio' ? 'form-field--with-radios' : ''}`}>
                <label className="form-field__label" htmlFor={field.id}>
                  {t(field.translationKey)}{field.required ? '*' : ''}
                </label>
                {field.type === 'tel' ? (
                  <PhoneInput
                    country={'ca'}
                    onlyCountries={['ca']}
                    disableCountryCode={true}
                    value={phone}
                    onChange={(value) => {
                      setPhone(value);
                      handleInputChange('phone', value);
                    }}
                    masks={{ ca: '(...) ...-....' }}
                    inputClass="form-field__input"
                    containerClass="form-field__phone-container"
                    buttonClass="form-field__phone-button"
                    dropdownClass="form-field__phone-dropdown"
                    disabled={false}
                  />
                ) : field.type === 'select' ? (
                  field.component ? (
                    <>
                      <field.component
                        value={formValues[field.id] || initialData[field.id]}
                        onChange={(option: any) => {
                          // Handle both string values and objects with value property
                          const value = typeof option === 'string' ? option : (option?.value || '');
                          console.log(`${field.id} changed to:`, value);
                          const formEl = document.getElementById(`hidden-${field.id}`) as HTMLInputElement;
                          if (formEl) {
                            formEl.value = value;
                            console.log(`Hidden input ${field.id} set to:`, formEl.value);
                          } else {
                            console.error(`Hidden input for ${field.id} not found`);
                          }
                          handleInputChange(field.id, value);
                        }}
                        isClearable={field.isClearable}
                        placeholder={field.placeholder}
                      />
                      <input
                        type="hidden"
                        id={`hidden-${field.id}`}
                        name={field.id}
                        value={formValues[field.id] || initialData[field.id] || ''}
                        readOnly
                      />
                    </>
                  ) : (
                    <select
                      id={field.id}
                      name={field.id}
                      className="form-field__select"
                      defaultValue={initialData[field.id] || (field.options?.[0]?.value ?? '')}
                      required={field.required}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                    >
                      {field.options?.map((option: { label: string; value: string }) => (
                        <option key={option.value} value={option.value}>
                          {t(option.label)}
                        </option>
                      ))}
                    </select>
                  )
                ) : field.type === 'radio' ? (
                  <div className="form-field__question-row">
                    <div className="radio-group radio-group--left">
                      {field.options?.map((option: { label: string; value: string }) => (
                        <label key={option.value} className="radio-option">
                          <input
                            type="radio"
                            name={field.id}
                            value={option.value}
                            defaultChecked={initialData[field.id] === option.value}
                            required={field.required}
                            onChange={(e) => handleInputChange(field.id, e.target.checked ? option.value : '')}
                          />
                          <span>{t(option.label)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      type={field.type}
                      id={field.id}
                      name={field.id}
                      className="form-field__input"
                      defaultValue={initialData[field.id]}
                      required={field.required}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

    </form>
  );
};
